import { motion } from 'framer-motion';
import { Heart, Shield, Sparkles, Compass, Sun, Flame } from 'lucide-react';

const fadeUp = {
  initial: { opacity: 0, y: 30 },
  whileInView: { opacity: 1, y: 0 },
  viewport: { once: true, margin: '-80px' },
  transition: { duration: 0.8, ease: [0.22, 1, 0.36, 1] },
};

export default function AboutPage() {
  return (
    <div data-testid="about-page">
      {/* Page Header */}
      <section className="py-16 md:py-24 bg-[#FDFBF7]">
        <div className="max-w-3xl mx-auto px-6 md:px-12 text-center">
          <motion.div {...fadeUp}>
            <p className="font-sans text-xs tracking-[0.3em] uppercase text-[#A67A5B] mb-4">
              Our Story
            </p>
            <h1 className="font-serif text-4xl sm:text-5xl font-semibold text-[#2A2421] mb-6 leading-tight">
              About Led By Faith Studios
            </h1>
            <p className="font-sans text-base md:text-lg text-[#7A736E] leading-relaxed max-w-2xl mx-auto">
              Led By Faith Studios is a faith-based publishing and creative brand built on testimony, resilience, and a deep belief that God uses the broken places to build something beautiful. Every book, journal, and project we create is rooted in real life, real faith, and real purpose.
            </p>
          </motion.div>
        </div>
      </section>

      {/* Founder Section */}
      <section className="py-16 md:py-24 bg-white">
        <div className="max-w-3xl mx-auto px-6 md:px-12">
          <motion.div {...fadeUp}>
            <div className="w-12 h-[1px] bg-[#A67A5B] mx-auto mb-10" />

            <div className="text-center mb-8">
              <p className="font-sans text-xs tracking-[0.2em] uppercase text-[#A67A5B] mb-2">Founder</p>
              <h2 className="font-serif text-3xl sm:text-4xl font-semibold text-[#2A2421]">
                Alzadia L. Spires
              </h2>
            </div>

            <div className="space-y-5 font-sans text-base text-[#7A736E] leading-relaxed">
              <p>
                Alzadia L. Spires is the founder of Led By Faith Studios and the creator behind a growing collection of faith-centered books, journals, and products designed to encourage, heal, and inspire. She is also the founder of <strong>Cuavena</strong>, the brand's curated online storefront &mdash; a home for meaningful, useful, and beautiful items aligned with faith, inspiration, and intentional living.
              </p>
              <p>
                A single mother of two remarkable young men, Gregory Spires Jr. and Jarvis Spires, Alzadia has built this brand from a place that many people would not expect &mdash; not from comfort, but from the fire. Her journey through adversity, loss, and life-threatening experiences did not destroy her. It sharpened her. It deepened her faith. And it gave her a voice that refuses to be silent.
              </p>
              <p>
                Led By Faith Studios was born out of that voice. Every page, every project, and every product carries a piece of the testimony that God wrote through her life &mdash; a testimony of survival, gratitude, resilience, and unwavering trust in a God who never once let go.
              </p>
              <p>
                Alzadia's mission is simple and clear: to create work that meets people where they are, reminds them they are not alone, and gives them something real to hold onto. Whether it is a journal for quiet mornings with God, a book that tells a story the world needs to hear, or a product that brings beauty and purpose into someone's everyday life &mdash; everything she builds is grounded in faith and driven by vision.
              </p>
              <p className="font-serif text-lg italic text-[#2A2421]/70 text-center pt-2">
                "I didn't start this brand because everything was easy. I started it because God told me to keep going."
              </p>
            </div>

            <div className="w-12 h-[1px] bg-[#A67A5B] mx-auto mt-10" />
          </motion.div>
        </div>
      </section>

      {/* Co-Founders Section */}
      <section className="py-16 md:py-24 bg-[#F5F2EB]">
        <div className="max-w-3xl mx-auto px-6 md:px-12">
          <motion.div {...fadeUp}>
            <p className="font-sans text-xs tracking-[0.2em] uppercase text-[#A67A5B] mb-8 text-center">
              Co-Founders
            </p>

            <div className="space-y-12">
              {/* Gregory */}
              <div data-testid="cofounder-gregory">
                <h3 className="font-serif text-2xl font-semibold text-[#2A2421] mb-1">
                  Gregory Spires Jr.
                </h3>
                <p className="font-sans text-xs tracking-[0.15em] uppercase text-[#A67A5B] mb-4">Co-Founder</p>
                <p className="font-sans text-base text-[#7A736E] leading-relaxed">
                  Gregory Spires Jr. carries himself with a quiet strength that speaks louder than words. Disciplined, driven, and grounded in purpose, he brings a steady hand to the heart of Led By Faith Studios. His resilience is not the kind that demands attention &mdash; it is the kind that holds everything together when the pressure builds. He is growing into a leader defined not by volume, but by character, consistency, and an unwavering commitment to the vision his family is building.
                </p>
              </div>

              {/* Jarvis */}
              <div data-testid="cofounder-jarvis">
                <h3 className="font-serif text-2xl font-semibold text-[#2A2421] mb-1">
                  Jarvis Spires
                </h3>
                <p className="font-sans text-xs tracking-[0.15em] uppercase text-[#A67A5B] mb-4">Co-Founder</p>
                <p className="font-sans text-base text-[#7A736E] leading-relaxed">
                  Jarvis Spires is a living testimony. His story &mdash; one of faith, survival, and spiritual strength &mdash; is woven into the very foundation of this brand. What he has walked through would have broken most people, but Jarvis carries it with grace and an unshakable sense of purpose. His journey continues to inspire everyone around him, and his presence in Led By Faith Studios is a reminder that miracles are not just stories we read about &mdash; they are stories we live.
                </p>
              </div>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Brand Mission */}
      <section className="py-16 md:py-24 bg-white">
        <div className="max-w-3xl mx-auto px-6 md:px-12 text-center">
          <motion.div {...fadeUp}>
            <h2 className="font-serif text-3xl font-semibold text-[#2A2421] mb-6">
              What We Stand For
            </h2>
            <p className="font-sans text-base text-[#7A736E] leading-relaxed mb-8 max-w-2xl mx-auto">
              Led By Faith Studios exists to create books, journals, and products that carry real testimony into the hands of real people. We believe that faith is not a formula &mdash; it is a daily decision to trust God even when the road is unclear. Everything we build reflects that belief.
            </p>
            <div className="flex flex-nowrap justify-center items-center gap-2 sm:gap-3 overflow-x-auto whitespace-nowrap px-2 -mx-2 md:mx-0 md:overflow-visible">
              {[
                { word: 'Faith',      Icon: Flame },
                { word: 'Resilience', Icon: Shield },
                { word: 'Gratitude',  Icon: Sparkles },
                { word: 'Purpose',    Icon: Compass },
                { word: 'Healing',    Icon: Sun },
                { word: 'Love',       Icon: Heart },
              ].map(({ word, Icon }) => (
                <span
                  key={word}
                  data-testid={`stand-for-${word.toLowerCase()}`}
                  className="inline-flex items-center gap-1.5 px-3 sm:px-4 py-1.5 sm:py-2 rounded-full bg-[#A67A5B]/8 border border-[#A67A5B]/15 text-xs sm:text-sm font-medium text-[#A67A5B] flex-shrink-0"
                >
                  <Icon size={13} strokeWidth={1.8} className="text-[#A67A5B]" />
                  {word}
                </span>
              ))}
            </div>
          </motion.div>
        </div>
      </section>

      {/* The Family Behind the Mission */}
      <section className="py-16 md:py-24 bg-[#FDFBF7]">
        <div className="max-w-2xl mx-auto px-6 md:px-12">
          <motion.div {...fadeUp} className="text-center">
            <h2 className="font-serif text-3xl sm:text-4xl font-semibold text-[#2A2421] mb-10">
              The Family Behind the Mission
            </h2>
            <div className="rounded-2xl overflow-hidden shadow-xl mb-6">
              <img
                src="https://customer-assets.emergentagent.com/job_faith-reads-3/artifacts/8wsme5i9_IMG_3350.jpeg"
                alt="The Spires Family"
                data-testid="family-photo"
                className="w-full h-auto"
              />
            </div>
            <p className="font-serif text-lg italic text-[#2A2421]/60">
              Rooted in faith. Carried by family.
            </p>
          </motion.div>
        </div>
      </section>
    </div>
  );
}
